"use client";

import { ArrowRight, PhoneCall, ShieldCheck, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DirectLine() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="1954" id="direct-line" className="relative overflow-hidden bg-card py-24 sm:py-32 border-y border-border">
      <h1 className="sr-only">Get a Free Tree Care Quote from DFW Experts</h1>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-accent/10 via-transparent to-transparent pointer-events-none" />

      <div className="relative mx-auto max-w-5xl px-6 lg:px-8">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <span className="mb-4 inline-flex items-center gap-2 rounded-lg bg-accent/10 px-3 py-1 font-mono text-xs uppercase tracking-[0.2em] text-accent font-semibold border border-accent/20">
              <ShieldCheck className="size-4" />
              Direct Arborist Access
            </span>

            <h2 className="font-default text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-5xl tracking-tighter">
              Speak Directly with an ISA Certified Arborist Today
            </h2>

            <p className="mt-6 font-default text-lg leading-relaxed text-muted-foreground">
              Our complimentary on-site consultations provide a comprehensive health assessment of your residential trees. We identify potential structural risks, diagnose diseases, and provide expert treatment recommendations to ensure the longevity and safety of your DFW property.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/HomePage" className="inline-flex items-center rounded-lg bg-background px-3 py-1.5 text-xs font-medium text-foreground border border-border hover:border-accent transition-colors">
                Home
              </Link>
              <Link to="/OurExpertisePage" className="inline-flex items-center rounded-lg bg-background px-3 py-1.5 text-xs font-medium text-foreground border border-border hover:border-accent transition-colors">
                Our Expertise
              </Link>
              <Link to="/ResidentialTreeCarePage" className="inline-flex items-center rounded-lg bg-background px-3 py-1.5 text-xs font-medium text-foreground border border-border hover:border-accent transition-colors">
                Residential Tree Care
              </Link>
              <Link to="/HoaTreeCarePage" className="inline-flex items-center rounded-lg bg-background px-3 py-1.5 text-xs font-medium text-foreground border border-border hover:border-accent transition-colors">
                HOA Tree Care
              </Link>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background/50 border border-border">
                <Clock className="size-5 text-accent shrink-0" />
                <span className="text-sm font-medium text-foreground">Same-Day Response Available</span>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background/50 border border-border">
                <ShieldCheck className="size-5 text-accent shrink-0" />
                <span className="text-sm font-medium text-foreground">100% Free On-Site Assessment</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-center p-8 sm:p-10 rounded-lg bg-background shadow-lg border border-border relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-lg blur-2xl pointer-events-none" />
            
            <div className="mb-6 overflow-hidden rounded-lg border border-border">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arborist_consultation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arborist_consultation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arborist_consultation.png-wvc-sizes" alt="Certified arborist conducting a consultation" loading="lazy" className="w-full h-48 object-cover transition-transform duration-500 hover:scale-105" />
            </div>

            <h3 className="text-xl font-bold text-foreground mb-3">Ready for Expert Tree Care?</h3>
            <p className="text-muted-foreground text-sm mb-8">
              Connect with our certified professionals to schedule your tailored residential evaluation in Fort Worth and surrounding areas.
            </p>

            <div className="flex flex-col gap-4">
              <Button asChild size="lg" className="w-full rounded-lg bg-accent px-6 py-6 text-base font-bold text-accent-foreground shadow-md transition-all hover:bg-accent/90">
                <Link to="/GetQuotePage">
                  Request a Free Quote
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full rounded-lg px-6 py-6 text-base font-medium border-border hover:bg-muted">
                <Link to="/OurExpertisePage">
                  View Our Surgical Standards
                </Link>
              </Button>
            </div>

            <div className="mt-6 pt-6 border-t border-border flex items-center justify-center gap-2 text-xs font-mono text-muted-foreground uppercase tracking-wider">
              <PhoneCall className="size-3.5 text-accent" />
              <span>Priority scheduling for homeowners</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}